// theme/scripts/components/monogram.js

import axios from 'axios';
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { NextStep } from '../common/nextStep';
import embroideryImage from '../../assets/placement.png';
import embroiderySpImage from '../../assets/placement-sp.png';
import { CustomizeContext } from '../contexts/customize';

const baseURL = process.env.REACT_APP_API_URL;
const client = axios.create({ baseURL });

const MtmEmbroidery = ({ pocketData, placketData }) => {
  // ==== masters ====
  const [color, setColor] = useState([]);       // []
  const [font, setFont] = useState([]);         // []
  const [format, setFormat] = useState([]);     // []
  const [placement, setPlacement] = useState([]); // []
  const [text, setText] = useState([]);         // [{type:'SELECT'|'PERIOD'|'INPUT', ...}]

  // ==== selections ====
  const [isEmbroidery, setIsEmbroidery] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedFont, setSelectedFont] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [selectedInitial, setSelectedInitial] = useState('');
  const [message, setMessage] = useState(''); // INPUTタイプ用

  // reorder関連
  const [reorderApplied, setReorderApplied] = useState(false);
  const [pendingFormatValue, setPendingFormatValue] = useState(null);
  const [preselectedLetters, setPreselectedLetters] = useState({});

  const { customizes, setCustomizes } = useContext(CustomizeContext);

  // ===== 1) コンテキスト反映（差分がある時のみ）=====
  const lastCtxJSON = useRef('');
  useEffect(() => {
    const nextEmbroidery = {
      embroidery: { value: isEmbroidery },
      embroidery_color: {
        id: selectedColor?.id || '',
        value: selectedColor?.value || '',
        img: selectedColor?.img || ''
      },
      embroidery_type: {
        id: selectedFont?.id || '',
        value: selectedFont?.value || '',
        img: selectedFont?.img || ''
      },
      embroidery_place: {
        id: selectedPlacement?.id || '',
        value: selectedPlacement?.value || '',
        img: embroideryImage
      },
      embroidery_format: {
        id: selectedFormat?.id || '',
        value: selectedFormat?.value || ''
      },
      embroidery_initial: {
        value: applyFormatToInitial(selectedInitial, selectedFormat?.value)
      }
    };

    const json = JSON.stringify(nextEmbroidery);

    // embroideryOptions に変更がない場合は何もしない
    if (json === lastCtxJSON.current) return;

    lastCtxJSON.current = json;

    // 👇 context全体ではなく embroideryOptions のみ更新
    setCustomizes((prev) => ({
      ...prev,
      embroideryOptions: nextEmbroidery,
    }));
  }, [isEmbroidery, selectedColor, selectedFont, selectedPlacement, selectedFormat, selectedInitial]);

  // ドット付きフォーマットを適用する ("A.A." + "RX" → "R.X.")
  const applyFormatToInitial = (letters = '', format = '') => {
    if (!letters) return '';
    if (!format) return letters;
    let i = 0;
    return format.replace(/A/g, () => letters[i++] || '');
  };

  // ===== 2) マスター読み込み =====
  useEffect(() => {
    axios
      .all([
        axios.get(`${baseURL}/get_emb_color_master`),
        axios.get(`${baseURL}/get_emb_font_master`)
      ])
      .then(
        axios.spread((colors, fonts) => {
          setColor(colors.data.colors || []);
          setFont(fonts.data.letter_type || []);
        })
      )
      .catch(console.error);
  }, []);

  // ===== 3) placement 読み込み（placket/pocket 依存）=====
  useEffect(() => {
    if (!placketData) return;
    const getPlacement = async () => {
      try {
        const pocket = pocketData ? `?pocket_id=${pocketData.id}&pocket_value=${pocketData.value}` : '';
        const res = await client.get(
          `/get_embroidery_positions${pocket ? pocket + '&' : '?'}placket_id=${placketData.id}&placket_value=${placketData.value}`
        );
        setPlacement(res.data.letter_place || []);
      } catch (e) {
        console.error(e);
      }
    };
    getPlacement();
  }, [pocketData, placketData]);

  // ===== 4) format 読み込み（selectedFont 依存）=====
  useEffect(() => {
    if (!selectedFont) return;
    const getFormat = async () => {
      try {
        const res = await client.get(
          `${baseURL}/get_embroidery_letter_formats?letter_type=${selectedFont.id}`
        );
        setFormat(res.data.letter_format || []);
      } catch (e) {
        console.error(e);
      }
    };
    getFormat();
  }, [selectedFont]);

  // ===== 5) text 読み込み（selectedFormat/Font/Placement 依存）=====
  useEffect(() => {
    if (!selectedFormat || !selectedFont || !selectedPlacement) return;
    const getText = async () => {
      try {
        const res = await client.get(
          `${baseURL}/get_embroidery_letters?letter_format=${selectedFormat.id}&letter_type=${selectedFont.id}&letter_place=${selectedPlacement.id}`
        );
        setText(res.data.letter_options || []);
      } catch (e) {
        console.error(e);
      }
    };
    getText();
  }, [selectedFormat, selectedFont, selectedPlacement]);

  // ===== 6) reorder の先行反映（isEmbroidery だけ先に）=====
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('reorder_properties');
      if (!raw) return;
      const r = JSON.parse(raw);
      const embValue = String(r._Embroidery || '').toLowerCase().trim();
      setIsEmbroidery(embValue === 'true' || embValue === '1' || embValue === 'yes');
    } catch {}
  }, []);

  // ===== 7) reorder 本適用（色/フォント/位置が揃ったら一度だけ）=====
  const applyReorder = useCallback(() => {
    const raw = sessionStorage.getItem('reorder_properties');
    if (!raw) return;
    const r = JSON.parse(raw);

    // Color
    if (color.length && r._Embroidery_Color) {
      const matched = color.find(
        (c) =>
          c.label?.trim().toLowerCase() === r._Embroidery_Color?.trim().toLowerCase() ||
          c.color_cd?.toString() === r._Embroidery_Color ||
          c.id?.toString() === r._Embroidery_Color
      );
      if (matched) {
        setSelectedColor({
          id: matched.color_cd || matched.id,
          value: matched.label,
          img: matched.image_url
        });
      }
    }

    // Font
    if (font.length && r._Embroidery_Type) {
      const matched = font.find(
        (f) =>
          f.label?.trim().toLowerCase() === r._Embroidery_Type?.trim().toLowerCase() ||
          f.id?.toString() === r._Embroidery_Type
      );
      if (matched) {
        setSelectedFont({
          id: matched.id,
          value: matched.label,
          img: matched.image_url
        });
      }
    }

    // Placement
    if (placement.length && r._Embroidery_Place) {
      const matched = placement.find(
        (p) =>
          p.label?.trim().toLowerCase() === r._Embroidery_Place?.trim().toLowerCase() ||
          p.id?.toString() === r._Embroidery_Place
      );
      if (matched) {
        setSelectedPlacement({ id: matched.id, value: matched.label });
      }
    }

    // Format は format 読込後に適用
    if (r._Embroidery_Format) {
      setPendingFormatValue(r._Embroidery_Format);
    }

    // Text（先に全体文字列は保持）
    if (r._Embroidery_Text) {
      const cleanLetters = r._Embroidery_Text.replace(/[^A-Za-z]/g, '');
      setSelectedInitial(cleanLetters);
      setMessage(cleanLetters);
    }

    setReorderApplied(true);
  }, [color, font, placement]);

  useEffect(() => {
    if (reorderApplied) return;
    if (color.length && font.length && placement.length) {
      applyReorder();
    }
  }, [color.length, font.length, placement.length, reorderApplied]); // applyReorderは依存に入れない

  // ===== 8) pendingFormat の適用（format 読み込み後）=====
  useEffect(() => {
    if (!pendingFormatValue || !format.length) return;
    const matched = format.find(
      (fm) =>
        fm.name?.trim().toLowerCase() === pendingFormatValue.trim().toLowerCase() ||
        fm.id?.toString() === pendingFormatValue
    );
    if (matched) {
      setSelectedFormat({ id: matched.id, value: matched.name });
      setPendingFormatValue(null);
    }
  }, [format, pendingFormatValue]);

  // ===== 9) text への reorder 文字の再割当（`SELECT`箱だけに順番に）=====
  useEffect(() => {
    const raw = sessionStorage.getItem('reorder_properties');
    if (!raw || text.length === 0) return;

    const r = JSON.parse(raw);
    if (!r._Embroidery_Text) return;

    // ✅ すでに preselectedLetters に値がある場合は二重反映しない
    if (Object.keys(preselectedLetters).length > 0) return;

    const chars = r._Embroidery_Text.split('');
    let ptr = 0;
    const nextLetters = {};

    text.forEach((item, idx) => {
      if (item.type === 'SELECT') {
        while (ptr < chars.length && chars[ptr] === '.') ptr++;
        const key = item.id || `select-${idx}`;
        nextLetters[key] = chars[ptr] || '';
        ptr++;
      }
    });

    // ✅ preselectedLetters と selectedInitial を同時に更新
    setPreselectedLetters(nextLetters);
    setSelectedInitial(r._Embroidery_Text);
  }, [text]);

  // ===== 10) handlers =====
  const handleColor = (e) => {
    const input = e.currentTarget;
    const labelEl = input.closest('label');
    const imgEl = labelEl ? labelEl.querySelector('img') : null;
    setSelectedColor({
      id: input.id || '',
      value: input.value || '',
      img: imgEl ? imgEl.src : ''
    });
  };

  const handleFont = (e) => {
    const input = e.currentTarget;
    const labelEl = input.closest('label');
    const imgEl = labelEl ? labelEl.querySelector('img') : null;
    setSelectedFont({
      id: input.id || '',
      value: input.value || '',
      img: imgEl ? imgEl.src : ''
    });
    // フォーマット/テキストはフォント変更に追随して更新される
    setSelectedFormat(null);
    setText([]);
    setSelectedInitial('');
    setPreselectedLetters({});
  };

  const handlePlacement = (e) => {
    setMessage('');
    setSelectedPlacement({
      id: e.target.selectedOptions[0].id,
      value: e.target.value
    });
    // テキストは再取得によりリセット
    setSelectedInitial('');
    setPreselectedLetters({});
  };

  const handleFormat = (e) => {
    setSelectedFormat({
      id: e.target.selectedOptions[0].id,
      value: e.target.value
    });
    // テキストは再取得によりリセット
    setSelectedInitial('');
    setPreselectedLetters({});
  };

  // SELECT 箱の change（UI 側 index ではなく SELECT 箱の連番 selectIdx を使う）
  const handleLetterSelect = (uniqueKey, value) => {
    setPreselectedLetters((prev) => {
      const next = { ...prev, [uniqueKey]: value };
      setSelectedInitial(Object.values(next).join(''));
      return next;
    });
  };

  // INPUT タイプ（もし来る場合）
  const handleTextInput = (e) => {
    const filtered = e.target.value.replace(/[^a-zA-Z]/g, '');
    setMessage(filtered);
    setSelectedInitial(filtered);
  };

  // SELECT 変更や INPUT 変更のたびに selectedInitial を再構築
  useEffect(() => {
    const letters = text
      .filter((t) => t.type === 'SELECT')
      .map((t, idx) => preselectedLetters[t.id || idx] || '');
    setSelectedInitial(letters.join(''));
  }, [preselectedLetters, text]);

  // ===== UI =====
  const iconCheck = (
    <svg width="35" height="26" viewBox="0 0 35 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 12.2L12.5283 23L33 2" stroke="white" strokeWidth="3" />
    </svg>
  );

  return (
    <div className="mtm-item__wrapper" id="mtm-monograms">
      <h3 className="mtm-embroidery__title">MONOGRAMS</h3>

      {/* Embroidery ON/OFF */}
      <div className="row mtm-embroidery-row">
        <div className="mtm-item__sub-title mb-3 text-start">EMBROIDERY</div>

        {/* With embroidery */}
        <input
          type="radio"
          id="with"
          name="embroidery"
          value="with"
          hidden
          checked={isEmbroidery === true}
          onChange={() => setIsEmbroidery(true)}
        />
        <label className="mtm--embroidery-select d-flex align-items-center col-12 col-md-4 py-4" htmlFor="with">
          <div className="mtm--embroidery-select_checkbox me-3" />
          <span>With Embroidery</span>
        </label>

        {/* Without embroidery */}
        <input
          type="radio"
          id="without"
          name="embroidery"
          value="without"
          hidden
          checked={isEmbroidery === false}
          onChange={() => setIsEmbroidery(false)}
        />
        <label className="mtm--embroidery-select d-flex align-items-center col-12 col-md-4 py-4" htmlFor="without">
          <div className="mtm--embroidery-select_checkbox me-3" />
          <span>Without Embroidery</span>
        </label>
      </div>

      {isEmbroidery && (
        <>
          {/* COLORS */}
          <h3 className="mtm-item__sub-title mt-5 text-md-left">COLORS</h3>
          <div className="row row-cols-2 row-cols-md-5">
            {color.map((option, idx) => (
              <div className="col" key={`color-${option.color_cd || option.id || idx}`}>
                <input
                  type="radio"
                  id={option.color_cd}
                  name="colors"
                  value={option.label}
                  hidden
                  onChange={handleColor}
                  checked={selectedColor?.id === (option.color_cd || option.id)}
                />
                <label htmlFor={option.color_cd} className="mtm--embroidery-colors">
                  <div className="mtm--embroidery_img-wrapper mb-4">
                    <div className="mtm--embroidery_selected d-flex flex-column align-items-center justify-content-center">
                      {iconCheck}
                      <span className="mt-1">Selected</span>
                    </div>
                    <img className="w-100" src={option.image_url} alt={option.label} />
                  </div>
                  <span>{option.label}</span>
                </label>
              </div>
            ))}
          </div>

          {/* FONTS */}
          <h3 className="mtm-item__sub-title mt-5 text-md-left">FONTS</h3>
          <div className="row row-cols-1 row-cols-md-5">
            {font.map((option, idx) => (
              <div className="col" key={`font-${option.id || idx}`}>
                <input
                  type="radio"
                  id={option.id}
                  name="fonts"
                  value={option.label}
                  hidden
                  onChange={handleFont}
                  checked={selectedFont?.id === option.id}
                />
                <label htmlFor={option.id} className="mtm--embroidery-colors">
                  <div className="mtm--embroidery_img-wrapper mb-4">
                    <div className="mtm--embroidery_selected d-flex flex-column align-items-center justify-content-center">
                      {iconCheck}
                      <span className="mt-1">Selected</span>
                    </div>
                    <img className="w-100" src={option.image_url} alt={option.label} />
                  </div>
                  <span>{option.label}</span>
                </label>
              </div>
            ))}
          </div>

          {/* PLACEMENT / FORMAT / TEXT */}
          <h3 className="mtm-item__sub-title mt-5 text-md-left">PLACEMENT</h3>
          <div className="mtm--row_embroidery row">
            <div className="mtm--row_embroidery--wrap col-12 col-md-4">
              {/* Location */}
              <p>Location</p>
              <select
                className="mtm--row_embroidery--select"
                onChange={handlePlacement}
                value={selectedPlacement?.value || ''}
              >
                <option hidden value="">-</option>
                {placement.map((option) => (
                  <option value={option.label} id={option.id} key={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>

              {/* Format */}
              <p>Format</p>
              <select
                className="mtm--row_embroidery--select"
                onChange={handleFormat}
                value={selectedFormat?.value || ''}
              >
                <option hidden value="">-</option>
                {format.map((option) => (
                  <option value={option.name} id={option.id} key={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>

              {/* Text */}
              <p>Text</p>
              {text && text.length > 0 ? (
                <div className="mtm--row_embroidery--select-wrap d-flex align-items-center p-0">
                  {text.map((option, index) => {
                    if (option.type === 'SELECT') {
                      const key = option.id || `select-${index}`;
                      return (
                        <select
                          key={key}
                          className="mtm--row_embroidery--select-font"
                          value={preselectedLetters[key] || ''}
                          onChange={(e) => handleLetterSelect(key, e.target.value)}
                        >
                          <option hidden value="">-</option>
                          {option.options.map((letter) => (
                            <option key={letter.id} value={letter.value}>{letter.label}</option>
                          ))}
                        </select>
                      );
                    } else if (option.type === 'PERIOD') {
                      return <span key={`dot-${index}`}>.</span>;
                    }
                  })}
                </div>
              ) : (
                <div className="mtm--row_embroidery--select-wrap d-flex align-items-center p-0">
                  <select className="mtm--row_embroidery--select-font"><option hidden>-</option></select>
                  <span>.</span>
                  <select className="mtm--row_embroidery--select-font"><option hidden>-</option></select>
                  <span>.</span>
                  <select className="mtm--row_embroidery--select-font"><option hidden>-</option></select>
                </div>
              )}
            </div>

            {/* image */}
            <img className="col-7 mtm--row_embroidery--img pc" src={embroideryImage} alt="mtm-embroidery" />
            <img className="w-100 mtm--row_embroidery--img sp" src={embroiderySpImage} alt="mtm-embroidery" />
          </div>
        </>
      )}

      <NextStep />
    </div>
  );
};

export default MtmEmbroidery;
